// Message attachment upload service for /api/v1/attachments/upload endpoint
import { API_CONFIG, API_ENDPOINTS } from '../helpers/constants';

export interface MessageAttachmentUploadResponse {
  attachment_id: string;
  filename: string;
  content_type?: string;
  size_bytes?: number;
}

export interface MessageAttachmentUploadResult {
  success: boolean;
  attachments: MessageAttachmentUploadResponse[];
  attachment_ids: string[];
}

/**
 * Upload a single file to /api/v1/attachments/upload endpoint
 * @param file File object to upload
 * @param authToken Authentication token
 * @param channelId Channel ID (required by API)
 * @param threadId Optional thread ID
 * @returns Promise with attachment response
 */
const uploadSingleFile = async (
  file: File,
  authToken: string,
  channelId: string,
  threadId?: string
): Promise<MessageAttachmentUploadResponse> => {
  const formData = new FormData();
  
  // Append single file - API expects 'file' field
  formData.append('file', file);

  // Build URL with channel_id as query parameter
  const url = new URL(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.ATTACHMENTS.UPLOAD}`);
  url.searchParams.append('channel_id', channelId);
  
  // Append optional thread_id as query parameter if provided
  if (threadId) {
    url.searchParams.append('thread_id', threadId);
  }

  const response = await fetch(
    url.toString(),
    {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        // Don't set Content-Type - browser will set it with boundary for FormData
      },
      body: formData,
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    
    let errorMessage = `Attachment upload failed: ${response.status}`;
    
    if (errorData.detail) {
      if (typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
        const firstError = errorData.detail[0];
        if (firstError.msg) {
          errorMessage = firstError.msg;
        } else if (typeof firstError === 'string') {
          errorMessage = firstError;
        }
      }
    }
    
    throw new Error(errorMessage);
  }

  const responseData = await response.json();
  
  // Handle API response format
  // API returns: { id, filename, original_filename, mime_type, file_size, ... }
  if (responseData.id) {
    return {
      attachment_id: responseData.id,
      filename: responseData.original_filename || responseData.filename || file.name,
      content_type: responseData.mime_type || responseData.content_type,
      size_bytes: responseData.file_size || responseData.size_bytes || file.size,
    };
  } else if (responseData.attachment_id) {
    // Fallback: handle if API returns attachment_id directly
    return {
      attachment_id: responseData.attachment_id,
      filename: responseData.filename || responseData.original_filename || file.name,
      content_type: responseData.content_type || responseData.mime_type,
      size_bytes: responseData.size_bytes || responseData.file_size || file.size,
    };
  } else if (Array.isArray(responseData) && responseData.length > 0) {
    // If response is an array, take the first item
    const item = responseData[0];
    return {
      attachment_id: item.id || item.attachment_id,
      filename: item.original_filename || item.filename || file.name,
      content_type: item.mime_type || item.content_type,
      size_bytes: item.file_size || item.size_bytes || file.size,
    };
  } else {
    throw new Error('Invalid response format from upload API');
  }
};

/**
 * Upload multiple files to /api/v1/attachments/upload endpoint
 * Calls the API once for each file
 * @param files Array of File objects to upload
 * @param authToken Authentication token
 * @param channelId Channel ID (required by API)
 * @param threadId Optional thread ID
 * @returns Promise with attachment IDs
 */
export const uploadMessageAttachments = async (
  files: File[],
  authToken: string,
  channelId: string,
  threadId?: string
): Promise<MessageAttachmentUploadResult> => {
  try {
    const uploadPromises = files.map((file) =>
      uploadSingleFile(file, authToken, channelId, threadId)
    );

    // Upload all files in parallel
    const uploadedAttachments = await Promise.all(uploadPromises);

    const attachmentIds = uploadedAttachments.map((att) => att.attachment_id);

    return {
      success: true,
      attachments: uploadedAttachments,
      attachment_ids: attachmentIds,
    };
  } catch (error) {
    console.error('Message attachment upload error:', error);
    throw error;
  }
};

/** Response shape for GET /api/v1/attachments/{id} (message/regular channel attachments) */
export interface MessageAttachmentDetailsResponse {
  id?: string;
  attachment_id?: string;
  filename?: string;
  original_filename?: string;
  file_size?: number;
  size_bytes?: number;
  mime_type?: string;
  content_type?: string;
  [key: string]: any;
}

/**
 * Fetch attachment details from main API (/api/v1/attachments/{id}).
 * Use for message/regular channel attachments; email attachments use emailService.getAttachmentDetails.
 */
export const getMessageAttachmentDetails = async (
  attachmentId: string,
  authToken: string
): Promise<MessageAttachmentDetailsResponse> => {
  const url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.ATTACHMENTS.GET_DETAILS(attachmentId)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const msg = err?.detail ?? (typeof err?.message === 'string' ? err.message : null) ?? `Attachment details failed: ${response.status}`;
    throw new Error(typeof msg === 'string' ? msg : 'Attachment details failed');
  }
  const data = await response.json();
  return data?.data ?? data?.attachment ?? data;
};
